/**
 * Admin Dashboard Page
 * Displays monitoring metrics for admin users only.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, AdminMetricsResponse } from '../services/api';
import { Loader2, RefreshCw, Database, HardDrive, Users, DollarSign, TrendingUp, AlertCircle, ArrowLeft } from 'lucide-react';

export function AdminDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<AdminMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAdminMetrics();
      setMetrics(data);
      setLastUpdated(new Date().toLocaleString());
    } catch (err: any) {
      console.error('Failed to load admin metrics:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError(err?.message || 'Failed to load metrics');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes < 0) return 'N/A';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    if (num < 0) return 'N/A';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading admin metrics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Access Error</h2>
          </div>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <button
              onClick={loadMetrics}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Admin Monitoring Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              {lastUpdated && (
                <span className="text-sm text-gray-500">Last updated: {lastUpdated}</span>
              )}
              <button
                onClick={loadMetrics}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-8">
          {/* User Metrics */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">User Metrics</h2>
            </div>
            {metrics?.users && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">Total Users</div>
                  <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics.users.total)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">New Users (7d)</div>
                  <div className="text-2xl font-bold text-green-600">{formatNumber(metrics.users.new_users_7d)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">New Users (30d)</div>
                  <div className="text-2xl font-bold text-blue-600">{formatNumber(metrics.users.new_users_30d)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">User Types</div>
                  <div className="text-sm mt-1">
                    {Object.entries(metrics.users.by_user_type).map(([type, count]) => (
                      <div key={type} className="flex justify-between">
                        <span className="capitalize">{type}:</span>
                        <span className="font-semibold">{formatNumber(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {metrics?.users && (
              <div className="mt-4 bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Type</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Count</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(metrics.users.by_billing_type).map(([type, count]) => (
                      <tr key={type}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatNumber(count)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {metrics.users.total > 0 ? ((count / metrics.users.total) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Growth Trends */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Growth Trends</h2>
            </div>
            {metrics?.growth && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">Total Jobs</div>
                  <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics.growth.total_jobs)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">New Jobs (7d)</div>
                  <div className="text-2xl font-bold text-green-600">{formatNumber(metrics.growth.new_jobs_7d)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">New Jobs (30d)</div>
                  <div className="text-2xl font-bold text-blue-600">{formatNumber(metrics.growth.new_jobs_30d)}</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-sm text-gray-500">Total Resumes</div>
                  <div className="text-2xl font-bold text-gray-900">{formatNumber(metrics.growth.total_resumes)}</div>
                </div>
              </div>
            )}
          </section>

          {/* DynamoDB Metrics */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-orange-600" />
              <h2 className="text-xl font-semibold text-gray-900">DynamoDB Tables</h2>
            </div>
            {metrics?.dynamodb && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table Name</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Item Count</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(metrics.dynamodb).map(([tableName, tableMetrics]) => (
                      <tr key={tableName}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tableName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {formatNumber(tableMetrics.item_count)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {formatBytes(tableMetrics.size_bytes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            tableMetrics.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : tableMetrics.status === 'ERROR'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {tableMetrics.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* S3 Metrics */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <HardDrive className="w-5 h-5 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-900">S3 Buckets</h2>
            </div>
            {metrics?.s3 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bucket Name</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Object Count</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(metrics.s3).map(([bucketName, bucketMetrics]) => (
                      <tr key={bucketName}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{bucketName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {formatNumber(bucketMetrics.object_count)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {formatBytes(bucketMetrics.size_bytes)}
                          {bucketMetrics.size_mb > 0 && (
                            <span className="text-gray-400 ml-1">({bucketMetrics.size_mb} MB)</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Cost Estimate */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">Estimated AWS Costs</h2>
            </div>
            {metrics?.cost_estimate && (
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-500 mb-4">{metrics.cost_estimate.note}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">DynamoDB</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.dynamodb.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">S3</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.s3.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Lambda</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.lambda.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">API Gateway</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.api_gateway.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">CloudFront</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.cloudfront.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Cognito</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.cognito.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Bedrock (AI)</div>
                    <div className="text-lg font-semibold">${metrics.cost_estimate.bedrock.toFixed(2)}</div>
                  </div>
                  <div className="bg-blue-50 rounded p-2">
                    <div className="text-sm text-blue-600">Total Estimate</div>
                    <div className="text-xl font-bold text-blue-700">
                      ${metrics.cost_estimate.total_estimate.toFixed(2)} {metrics.cost_estimate.currency}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Footer Note */}
          <div className="text-center text-sm text-gray-500 py-4">
            <p>Note: For Route53 linking to admin.skillsnap.me/monitor, manual DNS setup is required.</p>
            <p className="mt-1">Metrics timestamp: {metrics?.timestamp}</p>
          </div>
        </div>
      </main>
    </div>
  );
}
